import { ValidationErrorDetailsSchema } from '@cult/schemas';

export class ApiError extends Error {
  /** Machine-readable code from the API error envelope (e.g. `bad_input`, `not_found`). */
  public readonly code: string | undefined;
  /** For validation errors: first message per dotted field path, e.g. `{ title: '...' }`. */
  public readonly fieldErrors: Record<string, string> | undefined;

  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    // Prefer the message from the API error body over the generic fallback.
    // Supports two shapes:
    //   • { message: "..." }           — NestJS default exception shape
    //   • { error: { message: "..." } } — custom HttpExceptionFilter shape
    const b = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
    const bodyMessage: string | undefined =
      typeof b?.message === 'string'
        ? b.message
        : typeof (b?.error as Record<string, unknown> | undefined)?.message === 'string'
          ? ((b?.error as Record<string, unknown>).message as string)
          : undefined;
    super(message ?? bodyMessage ?? `API error: ${status}`);
    this.name = 'ApiError';

    const envelope = b?.error as Record<string, unknown> | undefined;
    this.code = typeof envelope?.code === 'string' ? envelope.code : undefined;
    const details = ValidationErrorDetailsSchema.safeParse(envelope?.details);
    this.fieldErrors = details.success ? details.data.fields : undefined;
  }
}

const request = async (path: string, init?: RequestInit): Promise<Response> => {
  const url = path.startsWith('/api') ? path : `/api${path}`;
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'X-Requested-With': 'fetch',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }
  return res;
};

export const apiFetch = async <T = unknown>(path: string, init?: RequestInit): Promise<T> => {
  const res = await request(path, init);
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return (await res.json()) as T;
};

export const apiDownload = async (path: string, filename: string): Promise<void> => {
  const blob = await (await request(path)).blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking synchronously can cancel the download in some browsers before it starts.
  setTimeout(() => URL.revokeObjectURL(url));
};
